/**
 * `useTheme` — Task 15.
 *
 * Fetches `/api/theme` once on mount, injects the assembled CSS into a
 * single `<style data-theme-bundle>` element in `<head>`, and toggles the
 * `theme-dark` / `theme-light` class on `<html>` per the bundle's
 * `themeClass`. When the bundle's `baseTheme` is `'system'`, the hook
 * observes `prefers-color-scheme` and flips the class live.
 *
 * Failure model — the Task 15 acceptance "themes referencing internal-only
 * selectors degrade gracefully (no crash)" extends to network failures:
 *   - `/api/theme` 5xx or network unreachable → hook leaves the document's
 *     existing class alone (the Shell already applied `theme-dark`) and
 *     surfaces `status: 'error'` for any caller that wants to badge it.
 *   - Empty bundle (no `.obsidian/`) → injects an empty `<style>` element
 *     and applies the default class. This is a successful no-op.
 *
 * The hook is idempotent: re-running it (React strict-mode double-invoke
 * or HMR) does not duplicate the `<style>` element — it reuses or replaces
 * the existing one.
 */

import { useEffect, useState } from 'react';

/* ── Wire format (mirrored from apps/server/src/routes/theme.ts) ──────── */

export interface ThemeApiDiagnostic {
  readonly code: string;
  readonly message: string;
  readonly path?: string;
}

export interface ThemeApiResponse {
  readonly baseTheme: 'obsidian' | 'moonstone' | 'system';
  readonly themeClass: 'theme-dark' | 'theme-light' | null;
  readonly activeTheme: string | null;
  readonly snippets: readonly string[];
  readonly css: string;
  readonly diagnostics: readonly ThemeApiDiagnostic[];
}

/** Sentinel attribute on the injected `<style>` element. Public so the
 *  Shell tests and dev-tools can find the node. */
export const THEME_STYLE_ATTRIBUTE = 'data-theme-bundle';

export type UseThemeStatus = 'loading' | 'ready' | 'error';

export interface UseThemeOptions {
  /** Override the fetch URL — tests inject a data URL or mock endpoint. */
  readonly url?: string;
  /** Disable the hook (SSR / unit tests that just want the parser). */
  readonly enabled?: boolean;
  /** Override the fetch implementation (tests). */
  readonly fetchImpl?: typeof fetch;
}

export interface UseThemeResult {
  readonly status: UseThemeStatus;
  readonly bundle: ThemeApiResponse | null;
  readonly error: string | null;
}

/* ── Parser (defensive — the server is trusted but the network isn't) ── */

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Validate the inbound JSON. Returns `null` on any schema mismatch so a
 * malformed response (e.g. an HTML error page from a misconfigured proxy)
 * cannot poison the DOM with arbitrary string content.
 */
export function parseThemeResponse(raw: unknown): ThemeApiResponse | null {
  if (!isRecord(raw)) return null;
  const baseTheme = raw['baseTheme'];
  if (baseTheme !== 'obsidian' && baseTheme !== 'moonstone' && baseTheme !== 'system') {
    return null;
  }
  const themeClass = raw['themeClass'];
  if (themeClass !== 'theme-dark' && themeClass !== 'theme-light' && themeClass !== null) {
    return null;
  }
  const activeTheme = raw['activeTheme'];
  if (activeTheme !== null && typeof activeTheme !== 'string') return null;
  const snippetsRaw = raw['snippets'];
  if (!Array.isArray(snippetsRaw) || !snippetsRaw.every((s) => typeof s === 'string')) {
    return null;
  }
  const css = raw['css'];
  if (typeof css !== 'string') return null;
  const diagnosticsRaw = raw['diagnostics'] ?? [];
  if (!Array.isArray(diagnosticsRaw)) return null;
  const diagnostics: ThemeApiDiagnostic[] = [];
  for (const d of diagnosticsRaw) {
    if (!isRecord(d)) return null;
    if (typeof d['code'] !== 'string' || typeof d['message'] !== 'string') return null;
    const entry: ThemeApiDiagnostic =
      typeof d['path'] === 'string'
        ? { code: d['code'] as string, message: d['message'] as string, path: d['path'] as string }
        : { code: d['code'] as string, message: d['message'] as string };
    diagnostics.push(entry);
  }
  return {
    baseTheme,
    themeClass,
    activeTheme,
    snippets: snippetsRaw as string[],
    css,
    diagnostics,
  };
}

/* ── DOM application (pure-ish helpers, exported for tests) ──────────── */

/**
 * Idempotently install the theme CSS into the document head. Returns the
 * `<style>` element so callers can verify (in tests) that injection
 * happened and the bundle's css landed in `textContent`.
 *
 * Re-running this with new CSS replaces the previous element's content in
 * place — no flash of unstyled content, no duplicate elements.
 */
export function applyThemeCss(doc: Document, css: string): HTMLStyleElement {
  let el = doc.head.querySelector<HTMLStyleElement>(`style[${THEME_STYLE_ATTRIBUTE}]`);
  if (!el) {
    el = doc.createElement('style');
    el.setAttribute(THEME_STYLE_ATTRIBUTE, 'true');
    // Appending after any existing stylesheets so vault CSS overrides
    // built-in defaults (`obsidian-vars.css` ships first via the HTML
    // entry point, this bundle wins the cascade for shared properties).
    doc.head.appendChild(el);
  }
  el.textContent = css;
  return el;
}

/**
 * Apply the bundle's `themeClass` to the `<html>` element. When the bundle
 * leaves the class null (`baseTheme: 'system'`), we consult
 * `prefers-color-scheme` and pick the matching class.
 *
 * Removes any *other* theme class to keep the cascade unambiguous.
 */
export function applyThemeClass(
  doc: Document,
  themeClass: 'theme-dark' | 'theme-light' | null,
): 'theme-dark' | 'theme-light' {
  const html = doc.documentElement;
  let resolved: 'theme-dark' | 'theme-light';
  if (themeClass === null) {
    const prefersLight =
      typeof doc.defaultView?.matchMedia === 'function' &&
      doc.defaultView.matchMedia('(prefers-color-scheme: light)').matches;
    resolved = prefersLight ? 'theme-light' : 'theme-dark';
  } else {
    resolved = themeClass;
  }
  if (resolved === 'theme-dark') {
    html.classList.add('theme-dark');
    html.classList.remove('theme-light');
  } else {
    html.classList.add('theme-light');
    html.classList.remove('theme-dark');
  }
  return resolved;
}

/* ── Hook ────────────────────────────────────────────────────────────── */

const DEFAULT_URL = '/api/theme';

/**
 * Load the vault's theme bundle and apply it. The hook is fire-and-forget
 * — re-mounting refetches, but the user-perceived state (DOM CSS + class)
 * is updated idempotently.
 */
export function useTheme(options: UseThemeOptions = {}): UseThemeResult {
  const enabled = options.enabled !== false;
  const url = options.url ?? DEFAULT_URL;
  const fetchImpl = options.fetchImpl ?? (typeof fetch === 'function' ? fetch : null);

  const [status, setStatus] = useState<UseThemeStatus>(enabled ? 'loading' : 'ready');
  const [bundle, setBundle] = useState<ThemeApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setStatus('ready');
      return;
    }
    if (!fetchImpl) {
      setStatus('error');
      setError('fetch is not available in this environment');
      return;
    }
    const controller =
      typeof AbortController === 'function' ? new AbortController() : null;
    let cancelled = false;

    const run = async (): Promise<void> => {
      try {
        const init: RequestInit = {
          headers: { accept: 'application/json' },
          ...(controller ? { signal: controller.signal } : {}),
        };
        const res = await fetchImpl(url, init);
        if (!res.ok) {
          throw new Error(`theme endpoint returned HTTP ${res.status}`);
        }
        const json = (await res.json()) as unknown;
        const parsed = parseThemeResponse(json);
        if (!parsed) {
          throw new Error('theme endpoint returned a malformed payload');
        }
        if (cancelled) return;
        if (typeof document !== 'undefined') {
          applyThemeCss(document, parsed.css);
          applyThemeClass(document, parsed.themeClass);
        }
        setBundle(parsed);
        setError(null);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        const name = err instanceof Error ? err.name : '';
        if (name === 'AbortError') return;
        setError(err instanceof Error ? err.message : String(err));
        setStatus('error');
      }
    };
    void run();
    return (): void => {
      cancelled = true;
      if (controller) controller.abort();
    };
  }, [enabled, url, fetchImpl]);

  // System theme listener — only relevant when baseTheme === 'system' and
  // we successfully loaded the bundle.
  useEffect(() => {
    if (!bundle || bundle.baseTheme !== 'system' || typeof window === 'undefined') return;
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (): void => {
      applyThemeClass(document, null);
    };
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return (): void => mq.removeEventListener('change', handler);
    }
    // Safari <14 path: addListener / removeListener.
    if (typeof (mq as { addListener?: (cb: () => void) => void }).addListener === 'function') {
      (mq as { addListener: (cb: () => void) => void }).addListener(handler);
      return (): void => {
        (mq as { removeListener: (cb: () => void) => void }).removeListener(handler);
      };
    }
    return;
  }, [bundle]);

  return { status, bundle, error };
}

export default useTheme;
